import { obbCorners } from '../../geo/polygon';
import type { Lot } from './types';
import type { Plot } from './plots';
import type { LotPlacer } from './lotPlacer';
import { BACK } from './backlots';
import { across } from './acrossPlot';
import { lotFrame } from './lotFrame';

/**
 * PLAN Lots: an unquayed bank is built with backs to the water. A house whose plot runs to a bank
 * too short for a courtyard and the least warehouse runs on to the bank itself: the Warmoesstraat's
 * lots run to the Damrak on Cornelis Anthonisz's plan of 1544, and its spijkers, house, office and
 * warehouse in one, lay on the water (Nikkels 2013, after the tax registers of 1543). A slanting
 * bank stops the run `step` by `step` until the house's back corners stand on land.
 */
export const BANK = { run: BACK.courtyard.min + BACK.depth.min, step: 0.5 } as const;

function deepened(house: Lot, by: number): Lot {
  const obb = across(house, -lotFrame(house).depth, by, 1);
  return { ...house, obb, corners: obbCorners(obb), depth: house.depth + by };
}

/** How far the house's back runs toward the bank, its whole width on land and clear; 0 for not at all. */
function runLength(plot: Plot, placer: LotPlacer): number {
  for (let to = plot.depth; to >= BANK.step; to -= BANK.step) {
    if (placer.fits(across(plot.lot, 0, to, 1))) return to;
  }
  return 0;
}

/** The houses, those on short bank plots run on to the water, and the plots still to build on. */
export function runHousesToBanks(
  houses: readonly Lot[],
  plots: readonly Plot[],
  placer: LotPlacer
): { readonly houses: readonly Lot[]; readonly plots: readonly Plot[] } {
  const runs = new Map<Lot, Lot>();
  for (const plot of plots) {
    if (!plot.bank || plot.depth >= BANK.run) continue;
    const by = runLength(plot, placer);
    if (by === 0) continue;
    const house = deepened(plot.lot, by);
    placer.add(house);
    runs.set(plot.lot, house);
  }
  return {
    houses: houses.map((house) => runs.get(house) ?? house),
    plots: plots.filter((plot) => !runs.has(plot.lot)),
  };
}
